import { Button, DatePicker, DropdownSelect } from '@components/common';
import { FormInput } from '@components/common/FormField';
import {
  AccountRecord,
  JournalSourceModule,
} from '@/renderer/types/accounting';
import React from 'react';
import { AccountingReportTab } from './accountingReports';
import { AccountingPageInfoButton } from '../AccountingHelp';

interface AccountingReportsToolbarProps {
  activeTab: AccountingReportTab;
  searchQuery: string;
  dateFrom: string;
  dateTo: string;
  sourceModule: JournalSourceModule | 'all';
  accountId: string;
  accounts: AccountRecord[];
  isRefreshing: boolean;
  canExport: boolean;
  onSearchChange: (value: string) => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onSourceModuleChange: (value: JournalSourceModule | 'all') => void;
  onAccountChange: (value: string) => void;
  onRefresh: () => void;
  onExport: () => void;
}

export function AccountingReportsToolbar({
  activeTab,
  searchQuery,
  dateFrom,
  dateTo,
  sourceModule,
  accountId,
  accounts,
  isRefreshing,
  canExport,
  onSearchChange,
  onDateFromChange,
  onDateToChange,
  onSourceModuleChange,
  onAccountChange,
  onRefresh,
  onExport,
}: AccountingReportsToolbarProps) {
  const showDateFilters = activeTab !== 'trial-balance';
  const showSourceFilter = activeTab !== 'trial-balance';

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Accounting reports
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Review ledger activity, balances, and account movement from one
            reporting workspace.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <AccountingPageInfoButton page="accounting-reports" />
          <Button
            variant="outline"
            onClick={onRefresh}
            isLoading={isRefreshing}
          >
            Refresh
          </Button>
          {canExport ? (
            <Button onClick={onExport}>Export current view</Button>
          ) : null}
        </div>
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-2 xl:grid-cols-5">
        <FormInput
          label="Search"
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search account, memo, or reference"
        />
        <DropdownSelect
          label="Account"
          value={accountId || 'all'}
          searchable
          placeholder="All accounts"
          options={[
            { value: 'all', label: 'All accounts' },
            ...accounts.map((a) => ({
              value: a.id,
              label: `${a.code} - ${a.name}`,
            })),
          ]}
          onChange={(v) => onAccountChange(v === 'all' ? '' : v)}
        />
        {showSourceFilter ? (
          <DropdownSelect
            label="Source module"
            value={sourceModule}
            placeholder="All sources"
            options={[
              { value: 'all', label: 'All sources' },
              { value: 'manual', label: 'Manual' },
              { value: 'sales', label: 'Sales' },
              { value: 'inventory', label: 'Inventory' },
              { value: 'accommodation', label: 'Accommodation' },
              { value: 'booking_com', label: 'Booking.com' },
              { value: 'system', label: 'System' },
            ]}
            onChange={(v) =>
              onSourceModuleChange(v as JournalSourceModule | 'all')
            }
          />
        ) : (
          <div />
        )}
        {showDateFilters ? (
          <>
            <DatePicker
              label="From"
              mode="single"
              value={dateFrom}
              onChange={onDateFromChange}
              placeholder="Select start date"
            />
            <DatePicker
              label="To"
              mode="single"
              value={dateTo}
              onChange={onDateToChange}
              placeholder="Select end date"
            />
          </>
        ) : (
          <>
            <div />
            <div />
          </>
        )}
      </div>
    </div>
  );
}
